import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  CircularProgress,
} from "@mui/material";
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation } from "@apollo/client";
import Swal from "sweetalert2";
import { GET_USER, GET_USER_BY_CLINIC_QUERY } from "../../user/gql/Query";
import clientGraphql from "../../../common/apollo-graphql";
import { UPDATE_CLINIC_USER_MUTATION } from "../../clinics/gql/Mutation";
import { HookFormTextField, MyTypography } from "../../common/components";
import NotificationOptions from "./NotificationOptions";
import PermissionOptions from "./PermissionOptions";
import RoleOptions from "../AddUser/RoleOptions";
import { yupResolver } from "@hookform/resolvers/yup";

export const validationSchema = yup.object().shape({
  firstname: yup.string().required('First name is required'),
  lastname: yup.string().required('Last name is required'),
});

export default function UpdateUserForm({
  updateRole = false,
  userUuid = null,
  clinicId,
  onClose,
}) {

  const [permissions, setPermissions] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const {
    data: userData,
    loading: userLoading,
  } = useQuery(clinicId ? GET_USER_BY_CLINIC_QUERY : GET_USER, {
    client: clientGraphql,
    variables: {
      uuid: userUuid,
      clinicId: clinicId
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !userUuid
  });

  const [saveUser, { loading: updateLoading }] = useMutation(
    UPDATE_CLINIC_USER_MUTATION,
    { client: clientGraphql }
  );

  // form submit
  const onSubmit = async (formData) => {
    try {
      let { permissions, notifications, ...rest } = formData;

      let newData = {
        ...rest,
        roleId: parseInt(rest.roleId)
      };

      if (Array.isArray(permissions)) {
        let str = permissions.map(v => (v.value ? "1" : "0")).reverse().join("");
        newData.permission = parseInt(str, 2) || 0;
      }

      if (Array.isArray(notifications)) {
        let str = notifications.map(v => (v.value ? "1" : "0")).reverse().join("");
        newData.notification = parseInt(str, 2) || 0;
      }

      await saveUser({
        client: clientGraphql,
        variables: {
          userUuid,
          user: JSON.parse(JSON.stringify(newData)),
          clinicId: clinicId,
        },
      });

      Swal.fire({
        icon: "success",
        text: "User updated successfully!",
      }).then(() => {
        reset();
        if (onClose) {
          onClose(true);
        }
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        text: "Failed to update user",
      });
    }
  };

  const handlePermissionChange = (value, permission, e) => {
    const updatedPermissions = permissions.map((p) =>
      p.key === permission.key ? { ...p, value } : p
    );

    setPermissions(updatedPermissions);
    setValue("permissions", updatedPermissions);
  };

  const handleNotificationChange = (value, notification, e) => {
    const updatedNotifications = notifications.map((p) =>
      p.key === notification.key ? { ...p, value } : p
    );

    setNotifications(updatedNotifications);
    setValue("notifications", updatedNotifications);
  };

  useEffect(() => {
    console.log(userData)
    if (userData?.user) {
      const user = userData.user;

      setValue("firstname", user.firstname);
      setValue("lastname", user.lastname);
      setValue("roleId", user.role?.id);
      setValue("permissions", user.permissions || []);
      setValue("notifications", user.notifications || []);

      setPermissions(user.permissions || []);
      setNotifications(user.notifications || [])
    }
  }, [userData, setValue]);

  if (userLoading) {
    return (
      <Grid container justifyContent="center" p={2}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item sm={6} xs={12}>
          <HookFormTextField
            id="firstname"
            name="firstname"
            schema="firstname"
            placeholder="Enter First Name"
            label="First Name *"
            autoComplete="firstname"
            control={control}
            errors={errors}
            fullWidth
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <HookFormTextField
            id="lastname"
            name="lastname"
            schema="lastname"
            placeholder="Enter Last Name"
            label="Last Name *"
            autoComplete="lastname"
            control={control}
            errors={errors}
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <MyTypography variant="h5" component="h5" color="text.primary">
            Role
          </MyTypography>
        </Grid>
        <Grid item sm={6} xs={12}>
          <RoleOptions
            control={control}
            errors={errors}
            name="roleId"
          />
        </Grid>

        {!updateRole && (
          <>
            <Grid item xs={12}>
              <MyTypography variant="h5" component="h5" color="text.primary">
                Permissions
              </MyTypography>
            </Grid>
            <PermissionOptions
              placeholder="Enter the Permissions"
              label="Permissions"
              permissions={permissions}
              handleChange={handlePermissionChange}
            />

            <Grid item xs={12}>
              <MyTypography variant="h5" component="h5" color="text.primary">
                Notifications
              </MyTypography>
            </Grid>

            <NotificationOptions
              placeholder="Enter the Notications"
              label="Notifications"
              notificationSettings={notifications}
              handleChange={handleNotificationChange}
            />
          </>
        )}

        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateLoading}
          >
            {updateLoading ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
